import { CountrySelect, InputNumberLocalized, ProcessingOverlay, SettingsSection } from '@components'

import {
  Checkbox,
  Color,
  Flex,
  FormItemsGroup,
  Icon,
  Input,
  NavListItemWithBlockDescription,
  Select,
  Space,
  Spacing,
} from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { CompanySearchField } from '@features/companies/elements/CompanySearchField'
import { isFrenchCompany } from '@features/companies/utils/isFrenchCompany'
import { mapCompanyToCompanyDetails } from '@features/companies/utils/mapCompanyToCompanyDetails'
import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'
import { shouldAllowVatNumberWhenExempt } from '@features/invoices/utils/shouldAllowVatNumberWhenExempt'
import { countryIdToRegistrationNoPlaceholderTKey } from '@features/invoicingSettings/constants/countryIdToRegistrationNoPlaceholderTKey'
import { countryIdToVatNoPlaceholderTKey } from '@features/invoicingSettings/constants/countryIdToVatNoPlaceholderTKey'
import { SiretPendingAssignmentCheckbox } from '@features/invoicingSettings/elements/SiretPendingAssignment'
import { FrenchLegalStatus } from '@features/invoicingSettings/enums/frenchLegalStatus'
import { useShouldShowSiretPendingAssignmentCheckbox } from '@features/invoicingSettings/hooks/useShouldShowSiretPendingAssignmentCheckbox'
import { LegalStatusDisplayItem } from '@features/invoicingSettings/types/legalStatusDisplayItem'
import { getFrenchLegalStatusOptions } from '@features/invoicingSettings/utils/getFrenchLegalStatusOptions'
import { Company, FrenchCompany } from '@modules-deprecated/app/companies/types'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { ChartOfAccountId } from '@modules-deprecated/app/organization/enums/chartOfAccountId'

import { useFormContext, useSegment } from '../../../../../../../../hooks'
import { useDebouncedCallback } from '../../../../../../../../hooks/useDebouncedCallback'
import { useResolveTaxAndRegistrationNoLabel } from '../../../../../../../../hooks/useResolveTaxAndRegistrationNoLabel'
import { useOrganizationInfoSettings } from '../../contexts/organizationInfoSettingsContext'
import { OrganizationInfoFormSchema } from '../../utils/formData'
import * as Styled from './styles'

export const BasicInfo = (): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { FormItem, getValues, reset, setValue, trigger, watch } = useFormContext<OrganizationInfoFormSchema>()
  const { isProcessing, isLoading, isLegalStatusAndSharedCapitalVisible } = useOrganizationInfoSettings()
  const { organization } = useUserOrganization()
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()
  const { vatNoLabel, registrationNoLabel } = useResolveTaxAndRegistrationNoLabel()
  const shouldShowSiretPendingCheckbox = useShouldShowSiretPendingAssignmentCheckbox()

  const isSiretPendingAssignmentValue = watch('isSiretPendingAssignment')
  const isVatExemptedValue = watch('isVatExempted')
  const legalStatusOption = watch('legalStatusOption')
  const registrationNoValue = watch('registrationNo')

  const countryId = organization?.countryId
  const allowVatNumberWhenExempt = shouldAllowVatNumberWhenExempt(countryId)
  const defaultRegistrationNoPlaceholder = countryId ? t(countryIdToRegistrationNoPlaceholderTKey[countryId]) : ''
  const vatNoPlaceholder = countryId ? t(countryIdToVatNoPlaceholderTKey[countryId]) : ''
  const isVatNumberDisabled = isVatExemptedValue && !allowVatNumberWhenExempt
  const showVatExemptCheckbox = organization?.chartOfAccountId !== ChartOfAccountId.Manual && !isLoading
  const registrationNoPlaceholder = isSiretPendingAssignmentValue
    ? t('external_invoices.missing_customer_compliance_details.modal.form.field.siret_number.placeholder')
    : defaultRegistrationNoPlaceholder

  const handleDebouncedVatNumberChange = useDebouncedCallback(() =>
    track('XXX - Invoicing Core - Organization Settings - VAT Number typed'),
  )

  const handleSelectCompany = useCallback(
    (company: Partial<Company> | Partial<FrenchCompany>) => {
      const companyDetails = mapCompanyToCompanyDetails(company, countryId)
      const currentVatNo = getValues('vatNo')
      const isVatNoAutoFilled = getValues('autoCompletedFields')?.vatNo
      const isFrench = isFrenchCompany(company)
      const shouldUpdateVatNo = isFrench && !isVatExemptedValue && (!currentVatNo || isVatNoAutoFilled)

      reset({
        ...getValues(),
        city: companyDetails.companyCity,
        registrationNo: companyDetails.registrationNo,
        street: companyDetails.companyAddress,
        zipcode: companyDetails.companyZipCode,
        ...(isFrenchComplianceMode && {
          isSiretPendingAssignment: companyDetails.isSiretPendingAssignment,
          legalStatusCustom: companyDetails.legalStatusCustom,
          legalStatusOption: companyDetails.legalStatusOption,
        }),
        ...(shouldUpdateVatNo && { vatNo: companyDetails.vatNo || '' }),
      })

      // Ensure the reset has been fully applied before setting `autoCompletedFields`, so the highlight state is not wiped out.
      window.requestAnimationFrame(() => {
        setValue('autoCompletedFields', {
          city: !!companyDetails.companyCity,
          registrationNo: !!companyDetails.registrationNo,
          street: !!companyDetails.companyAddress,
          zipcode: !!companyDetails.companyZipCode,
          ...(isFrenchComplianceMode && {
            legalStatusCustom: !!companyDetails?.legalStatusCustom,
            legalStatusOption: !!companyDetails?.legalStatusOption,
          }),
          ...(shouldUpdateVatNo && { vatNo: !!companyDetails.vatNo }),
        })
      })
    },
    [getValues, countryId, isFrenchComplianceMode, isVatExemptedValue, reset, setValue],
  )

  return (
    <SettingsSection.Content title={t('settings.organization.organization_info.basic_info.heading')}>
      <Styled.BasicInfoFormWrapper>
        {(isProcessing || isLoading) && <ProcessingOverlay color={Color.White} />}
        <FormItemsGroup rowSpacing={Spacing.S16}>
          <FormItem
            name="name"
            label={t('settings.organization.organization_info.basic_info.name.label')}
            render={({ field }) => (
              <CompanySearchField {...field} autoFocus={false} onSelectCompany={handleSelectCompany} size="md" />
            )}
          />

          {isLegalStatusAndSharedCapitalVisible && (
            <Flex flexDirection="column" gap={Spacing.S10}>
              <FormItemsGroup itemsInRow={2} columnSpacing={Spacing.S12}>
                <FormItem
                  name="legalStatusOption"
                  label={t('settings.organization.organization_info.basic_info.legal_status.label')}
                  render={({ field: { value, onChange, ...props } }) => (
                    <Select<LegalStatusDisplayItem>
                      {...props}
                      items={getFrenchLegalStatusOptions(t)}
                      dropdownItemRender={(props) => <NavListItemWithBlockDescription {...props} />}
                      dropdownSize="xxxl"
                      dropdownMaxHeight="fullScreen"
                      onSelect={(id?: string) => onChange(id)}
                      placement="bottom-start"
                      selectedId={value}
                      withSearch={false}
                    />
                  )}
                />

                <FormItem
                  name="sharedCapital"
                  label={t('settings.organization.organization_info.basic_info.shared_capital.label')}
                  render={({ field }) => (
                    <InputNumberLocalized
                      {...field}
                      alignment="left"
                      isFallbackToZero={false}
                      min={0}
                      placeholder="0.00"
                      suffix={<Icon icon="euro" />}
                    />
                  )}
                />
              </FormItemsGroup>
              {legalStatusOption === FrenchLegalStatus.Other && (
                <FormItem
                  name="legalStatusCustom"
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder={t(
                        'settings.organization.organization_info.basic_info.legal_status_custom.placeholder',
                      )}
                    />
                  )}
                />
              )}
            </Flex>
          )}

          <FormItem
            name="registrationNo"
            label={registrationNoLabel}
            render={({ field }) => (
              <Input
                {...field}
                disabled={isSiretPendingAssignmentValue}
                placeholder={registrationNoPlaceholder}
                prefix={isSiretPendingAssignmentValue && <Icon icon="info" />}
              />
            )}
          />

          {shouldShowSiretPendingCheckbox && (
            <FormItem
              name="isSiretPendingAssignment"
              render={({ field: { value, ...field } }) => (
                <SiretPendingAssignmentCheckbox {...field} checked={value} registrationNoValue={registrationNoValue} />
              )}
            />
          )}

          {/**
           * For DK organizatons - we use TAX ID as registration number and do not show VAT number field
           */}
          {organization?.countryId !== 'DK' ? (
            <FormItem
              name="vatNo"
              label={vatNoLabel}
              render={({ field: { onChange, value, ...field } }) => (
                <Input
                  {...field}
                  disabled={isVatNumberDisabled}
                  onChange={(event) => {
                    handleDebouncedVatNumberChange()
                    onChange(event)
                  }}
                  placeholder={vatNoPlaceholder}
                  value={value ?? undefined}
                />
              )}
            />
          ) : null}
          {showVatExemptCheckbox && (
            <>
              <FormItem
                name="isVatExempted"
                render={({ field: { onChange, value, ...rest } }) => (
                  <Checkbox
                    {...rest}
                    checked={value}
                    onChange={(event) => {
                      track(
                        'XXX - Invoicing Core - Organization Settings - My company is VAT exempt. No VAT will be applied to my purchases or sales',
                        { checked: event.target.checked },
                      )
                      if (!allowVatNumberWhenExempt) {
                        setValue('vatNo', '')
                        trigger('vatNo')
                      }
                      onChange(event)
                    }}
                  >
                    {t('settings.organization.organization_info.vat_exempted')}
                  </Checkbox>
                )}
              />
              <Space size="s2" />
            </>
          )}
          <FormItem
            name="street"
            label={t('settings.organization.organization_info.basic_info.street.label')}
            render={({ field }) => (
              <Input
                {...field}
                placeholder={t('settings.organization.organization_info.basic_info.street.placeholder')}
              />
            )}
          />
          <FormItem
            name="addressLine2"
            render={({ field: { value, ...field } }) => (
              <Input
                {...field}
                value={value ?? undefined}
                placeholder={t('settings.organization.organization_info.basic_info.addressLine2.placeholder')}
              />
            )}
          />
          <FormItemsGroup itemsInRow={2}>
            <FormItem
              name="zipcode"
              itemColumnStart={1}
              itemColumnEnd={2}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder={t('settings.organization.organization_info.basic_info.zipcode.placeholder')}
                />
              )}
            />
            <FormItem
              name="city"
              itemColumnStart={2}
              itemColumnEnd={7}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder={t('settings.organization.organization_info.basic_info.city.placeholder')}
                />
              )}
            />
          </FormItemsGroup>
          <FormItem
            name="countryId"
            label={t('settings.organization.organization_info.basic_info.country.label')}
            render={({ field: { value, onChange, ...props } }) => (
              <CountrySelect {...props} dropdownSize="fitTrigger" onSelect={onChange} selectedId={value} disabled />
            )}
          />
        </FormItemsGroup>
      </Styled.BasicInfoFormWrapper>
    </SettingsSection.Content>
  )
}
